import {
  AutostorePickingState,
  AutostorePickingStateWithPortId,
  NextPickingBinResponse,
  PickingStatePickInfoByTote,
  PickingStateV2FocusedPick,
  PickingStateV2TotesInPosition,
  RepickProductsRequest,
  StartPickingOnWorkstationRequest
} from "~/types/api";

import { warehouseApi } from "./warehouseApi";

// sorts the totes (and their picks) in a picking response by tote position
export const shapePickingState = (
  pickingState: AutostorePickingState
): AutostorePickingState => {
  const sortedTotes = pickingState.totes.slice().sort((tote1, tote2) => {
    if (tote1.totePosition && tote2.totePosition) {
      return tote1.totePosition - tote2.totePosition;
    }
    return 0;
  });

  const allPicksSortedByTotesPosition = sortedTotes
    .map((tote) => {
      return pickingState.allPicks.find(
        (picks) => picks.toteId === tote.toteId
      );
    })
    .filter((ps): ps is PickingStatePickInfoByTote => !!ps);

  return {
    ...pickingState,
    allPicks: allPicksSortedByTotesPosition,
    totes: sortedTotes
  };
};

const autostorePickingApi = warehouseApi.injectEndpoints({
  endpoints: (build) => ({
    getPickingStateV1: build.query<
      AutostorePickingState,
      { gridId: string; portId: number; taskId?: number }
    >({
      query: (data) => {
        const { gridId, portId, taskId } = data;

        const taskQuery = taskId ? `/task/${taskId}` : "";

        return {
          url: `/autostore-grid/${gridId}/port/${portId}${taskQuery}/picking-state`
        };
      },
      transformResponse: shapePickingState,
      providesTags: ["picking port state"]
    }),
    getBatchPickingState: build.query<
      AutostorePickingState,
      { gridId: string; portId: number; batchId: Guid }
    >({
      query: ({ gridId, portId, batchId }) => ({
        url: `/autostore-grid/${gridId}/port/${portId}/picking-state`,
        params: { batchId }
      }),
      transformResponse: shapePickingState,
      providesTags: ["picking port state"]
    }),
    getFocusedPick: build.query<
      PickingStateV2FocusedPick | null,
      { workstationId: string }
    >({
      query: ({ workstationId }) => {
        return {
          url: `/autostore-picking/workstation-focused-pick/${workstationId}`
        };
      },
      providesTags: ["focused pick"]
    }),
    getTotesForBatch: build.query<
      PickingStateV2TotesInPosition[],
      { batchId: string }
    >({
      query: ({ batchId }) => {
        return {
          url: `/autostore-picking/totes-for-batch/${batchId}`
        };
      },
      providesTags: (result) =>
        result?.flatMap((positionTotes) =>
          positionTotes.totes?.map((tote) => ({
            type: "tote" as const,
            id: tote.toteId
          }))
        ) ?? []
    }),
    repickProducts: build.mutation<string, RepickProductsRequest>({
      query: (request) => {
        return {
          url: `/totes/repick-products`,
          method: "POST",
          body: request
        };
      },
      invalidatesTags: (_result, _error, { toteId }) => [
        { type: "tote", id: toteId },
        "picking port state"
      ]
    }),
    outOfStockPick: build.mutation<
      void,
      {
        pickId: string;
        pickBinId: string;
        autostoreGridId: string;
        autostorePortId: number;
        hold?: string;
      }
    >({
      query: ({
        pickId,
        pickBinId,
        autostoreGridId,
        autostorePortId,
        hold
      }) => {
        return {
          url: "/picks/out-of-stock",
          method: "POST",
          body: {
            pickId,
            pickBinId,
            autostoreGridId,
            autostorePortId,
            hold
          }
        };
      },
      invalidatesTags: ["picking port state"]
    }),
    pickBatch: build.mutation<
      AutostorePickingStateWithPortId[],
      {
        batchId: Guid;
        workstationId: Guid;
        gridId: Guid;
        portId?: number;
        enableMultiPort?: boolean;
      }
    >({
      query: ({ batchId, workstationId, gridId, portId, enableMultiPort }) => {
        return {
          url: "/autostore-picking/pick-batch",
          method: "POST",
          body: {
            batchId,
            workstationId,
            gridId,
            portId,
            enableMultiPort
          }
        };
      },
      invalidatesTags: ["picking port state"]
    }),
    startPickingOnWorkstation: build.mutation<
      AutostorePickingStateWithPortId | undefined,
      {
        gridId: Guid;
        workstationId: Guid;
      } & StartPickingOnWorkstationRequest
    >({
      query: ({ gridId, workstationId, ...request }) => {
        return {
          url: `/autostore-grid/${gridId}/workstation/${workstationId}/start-picking-on-workstation`,
          method: "POST",
          body: request
        };
      },
      transformResponse: (response: AutostorePickingStateWithPortId[]) => {
        const first = response.at(0);
        return (
          first && {
            ...first,
            pickingState: shapePickingState(first.pickingState)
          }
        );
      },
      invalidatesTags: ["picking port state"]
    }),
    nextPickBin: build.mutation<
      NextPickingBinResponse,
      { gridId: Guid; portId: number; workstationId?: Guid; deviceId?: string }
    >({
      query: ({ gridId, portId, workstationId, deviceId }) => {
        return {
          url: `/autostore-grid/${gridId}/port/${portId}/next-picking-bin`,
          method: "GET",
          params: { workstationId, deviceId }
        };
      },
      invalidatesTags: ["focused pick", "port status", "picking port state"]
    })
  }),
  overrideExisting: false
});

export default autostorePickingApi;
